import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { BusinessSearchResponse, CategoryItem } from '@/types';

function slugToTitle(slug: string): string {
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default function Category() {
  const { slug = '' } = useParams<{ slug: string }>();
  const categoryTitle = slugToTitle(slug);

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiClient<CategoryItem[]>('/public/categories'),
    staleTime: 3600_000,
    retry: false,
  });

  const category = categories?.find((c) => c.slug === slug);
  const searchTerm = category?.name ?? categoryTitle.replace(/ And /g, ' ');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['category-businesses', slug, searchTerm],
    queryFn: () =>
      apiClient<BusinessSearchResponse>('/public/businesses/search', {
        params: { q: searchTerm, page: 1, page_size: 24 },
      }),
    staleTime: 60_000,
    retry: 1,
  });

  return (
    <div className="container-page py-10">
      <nav className="text-sm text-slate-500">
        <Link to="/" className="hover:text-brand-primary">Home</Link>
        <span className="mx-2">/</span>
        <span className="text-slate-900">{category?.name ?? categoryTitle}</span>
      </nav>

      <h1 className="mt-4 text-3xl font-bold text-slate-900">
        {category?.name ?? categoryTitle}
      </h1>
      {category?.description && (
        <p className="mt-2 max-w-2xl text-slate-600">{category.description}</p>
      )}
      {!category && (
        <p className="mt-2 text-slate-600">
          Browse businesses in the {categoryTitle} category across India.
        </p>
      )}

      <div className="mt-10">
        {isLoading && (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-primary border-t-transparent" />
          </div>
        )}

        {isError && (
          <div className="rounded-brand border border-amber-200 bg-amber-50 p-6 text-amber-800">
            Could not load businesses. Try searching instead.
            <Link to="/search" className="ml-2 font-medium underline">
              Go to search
            </Link>
          </div>
        )}

        {data && (
          <>
            <p className="text-sm text-slate-500">{data.meta.total} businesses found</p>

            {data.data.length === 0 ? (
              <div className="mt-8 rounded-brand border border-slate-200 bg-white p-12 text-center">
                <p className="text-slate-600">No businesses in this category yet.</p>
                <Link to="/search" className="mt-4 inline-block text-sm font-medium text-brand-primary hover:underline">
                  Search all businesses
                </Link>
              </div>
            ) : (
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {data.data.map((business) => (
                  <article
                    key={business.id}
                    className="rounded-brand border border-slate-200 bg-white p-5 transition hover:border-brand-primary hover:shadow-sm"
                  >
                    <h2 className="font-semibold text-slate-900">{business.name}</h2>
                    <p className="mt-1 text-sm text-amber-600">★ {business.rating_avg.toFixed(1)}</p>
                    <Link
                      to={
                        business.state_slug && business.city_slug
                          ? `/india/${business.state_slug}/${business.city_slug}/${business.slug}`
                          : `/search?q=${encodeURIComponent(business.name)}`
                      }
                      className="mt-3 inline-block text-sm font-medium text-brand-primary hover:underline"
                    >
                      View details →
                    </Link>
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
